"use client"

import React, { useState, useEffect } from "react"
import { DonutChart } from "../../../components/ui/donut-chart"
import { Card } from "../../../components/ui/card"
import { motion, AnimatePresence } from "framer-motion"
import { cn } from "../../../lib/utils"
import { computeRevenueByProduct } from "../../../utils/dashboardAnalyticsHelpers"

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0)

const RevenueByProductCard = ({ orgId, dateRange, onNavigateTab }) => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [hoveredSegment, setHoveredSegment] = useState(null)

  useEffect(() => {
    if (!orgId) return
    setLoading(true)
    computeRevenueByProduct(orgId, dateRange).then((result) => {
      setData(result)
      setLoading(false)
    })
  }, [orgId, dateRange])

  const total = data?.reduce((sum, d) => sum + d.value, 0) || 0
  const activeSegment = data?.find((s) => s.label === hoveredSegment)
  const displayValue = activeSegment?.value ?? total
  const displayLabel = activeSegment?.label ?? "Total Revenue"
  const displayPercentage = activeSegment && total ? (activeSegment.value / total) * 100 : 100

  return (
    <Card className="p-6 w-full flex flex-col items-center justify-center space-y-6 shadow-none">
      <div className="flex items-center justify-between w-full">
        <h2 className="text-base font-semibold text-foreground">Revenue by Product</h2>
        <button onClick={() => onNavigateTab?.("commerce-products")} className="text-xs font-medium text-primary hover:underline">
          View Products
        </button>
      </div>

      {loading ? (
        <div className="h-[220px] w-[220px] rounded-full bg-muted animate-pulse" />
      ) : (
        <div className="relative flex items-center justify-center">
          <DonutChart
            data={data}
            size={220}
            strokeWidth={26}
            animationDuration={1.2}
            animationDelayPerSegment={0.05}
            highlightOnHover
            onSegmentHover={(seg) => setHoveredSegment(seg?.label ?? null)}
            centerContent={
              <AnimatePresence mode="wait">
                <motion.div
                  key={displayLabel}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.2, ease: "circOut" }}
                  className="flex flex-col items-center justify-center text-center"
                >
                  <p className="text-muted-foreground text-xs font-medium truncate max-w-[130px]">{displayLabel}</p>
                  <p className="text-3xl font-bold text-foreground">{formatCurrency(displayValue)}</p>
                  {activeSegment && <p className="text-sm font-medium text-muted-foreground">[{displayPercentage.toFixed(0)}%]</p>}
                </motion.div>
              </AnimatePresence>
            }
          />
        </div>
      )}

      <div className="flex flex-col space-y-2 w-full pt-4 border-t border-border">
        {data?.map((segment) => (
          <div
            key={segment.label}
            className={cn("flex items-center justify-between p-2 rounded-md transition-all duration-200 cursor-pointer", hoveredSegment === segment.label && "bg-muted")}
            onMouseEnter={() => setHoveredSegment(segment.label)}
            onMouseLeave={() => setHoveredSegment(null)}
          >
            <div className="flex items-center space-x-3">
              <span className="h-3 w-3 rounded-full" style={{ backgroundColor: segment.color }} />
              <span className="text-sm font-medium text-foreground">{segment.label}</span>
            </div>
            <span className="text-sm font-semibold text-muted-foreground">{formatCurrency(segment.value)}</span>
          </div>
        ))}
      </div>
    </Card>
  )
}

export default RevenueByProductCard